import { collection, doc, getDoc, getDocs, setDoc, deleteDoc, Timestamp, type DocumentReference } from 'firebase/firestore';
import { autoIncrementSemanticVersion } from '@/lib/utils';
import { db, auth } from '@/lib/firebase';
import { getAuthUID } from '@/lib/authState';
import { Policy, PolicyListItem } from '@/types/policy';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '@/lib/firebase';
import { getDocuments, saveDocument, createDocument, getDocument } from './documentsService';
import { SupportingDocs } from '../js/external-js/variableModules/namespaces/SupportingDocs.namespace';
import { convertStatus } from '@/app/pages/documentlibrary';
import { cleanDateAndTimeObject } from '@shared/renderDate';
import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';

function dedupeArray<T>(arr: T[]): T[] {
  return Array.from(new Set(arr));
}

// Safely convert various date-like inputs (Firestore Timestamp, Date, string, number, or {seconds}) to a valid Date or null
function toDateSafe(value: any): Date | null {
  try {
    if (!value) return null;
    if (value instanceof Timestamp) return value.toDate();
    if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
    // Handle plain object with seconds (e.g., serialized Timestamp)
    if (typeof value === 'object' && typeof value.seconds === 'number') {
      const d = new Date(value.seconds * 1000);
      return isNaN(d.getTime()) ? null : d;
    }
    if (typeof value === 'number') {
      // If looks like seconds, convert; else assume ms
      const ms = value < 1e12 ? value * 1000 : value;
      const d = new Date(ms);
      return isNaN(d.getTime()) ? null : d;
    }
    if (typeof value === 'string') {
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    }
    return null;
  } catch {
    return null;
  }
}

// Only treat keys that look like date/time fields as date-like when sanitizing primitives
const DATE_KEY_REGEX = /(date|time|edited|reviewed|uploaded|created|lastEdited|updated)/i;

function sanitizeDatesDeep(obj: any, seen = new Set<unknown>()): any {
  // Handle primitive types and null/undefined
  if (obj === null || obj === undefined) return obj;

  // Handle circular references for objects
  if (typeof obj === 'object') {
    if (seen.has(obj)) return obj; // Return as-is to prevent infinite recursion
    seen.add(obj);
  }

  // Process the object
  if (obj instanceof Timestamp) return obj.toDate();
  if (obj instanceof Date) return isNaN(obj.getTime()) ? null : obj;
  if (Array.isArray(obj)) return obj.map(item => sanitizeDatesDeep(item, new Set(seen)));

  if (typeof obj !== 'object') return obj;

  const result: any = Array.isArray(obj) ? [] : {};
  for (const [k, v] of Object.entries(obj)) {
    if (
      v instanceof Date ||
      v instanceof Timestamp ||
      (v && typeof v === 'object' && typeof (v as any).seconds === 'number') ||
      ((typeof v === 'string' || typeof v === 'number') && DATE_KEY_REGEX.test(k))
    ) {
      const d = toDateSafe(v);
      result[k] = d !== null ? d : null;
    } else if (Array.isArray(v)) {
      result[k] = v.map(item => sanitizeDatesDeep(item, new Set(seen)));
    } else if (v && typeof v === 'object') {
      result[k] = sanitizeDatesDeep(v, new Set(seen));
    } else {
      result[k] = v;
    }
  }
  return result;
}

function sanitizePolicyForWrite(policy: any): any {
  // First, handle Firestore objects and functions
  const cleaned = JSON.parse(JSON.stringify(policy, (key, value) => {
    // Skip functions
    if (typeof value === 'function') {
      return undefined;
    }
    // Handle Firestore Timestamp
    if (value && typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
      return value.toDate();
    }
    // Handle Firestore DocumentReference
    if (value && typeof value === 'object' && 'path' in value && 'id' in value) {
      return { _firestore: true, path: value.path, id: value.id };
    }
    // Handle Firestore FieldValue objects
    if (value && typeof value === 'object' && 'isEqual' in value) {
      return undefined;
    }
    return value;
  }));

  // Then sanitize dates in the cleaned object
  const withSanitizedDates = sanitizeDatesDeep(cleaned);

  // Ensure critical fields exist and are valid
  if (withSanitizedDates?.policy_metadata) {
    const le = toDateSafe(withSanitizedDates.policy_metadata.lastEdited);
    withSanitizedDates.policy_metadata.lastEdited = le || new Date();
  }

  if (withSanitizedDates?.policy_history) {
    withSanitizedDates.policy_history.created_date =
      toDateSafe(withSanitizedDates.policy_history.created_date) ||
      withSanitizedDates.policy_history.created_date ||
      null;

    withSanitizedDates.policy_history.last_reviewed =
      toDateSafe(withSanitizedDates.policy_history.last_reviewed) ||
      toDateSafe(withSanitizedDates.policy_history.created_date) ||
      withSanitizedDates.policy_history.last_reviewed ||
      null;

    if (withSanitizedDates.policy_history.updated_date !== undefined) {
      withSanitizedDates.policy_history.updated_date =
        toDateSafe(withSanitizedDates.policy_history.updated_date) ||
        withSanitizedDates.policy_history.updated_date ||
        null;
    }
  }

  return withSanitizedDates;
}
export async function getDocPolicies(companyID: string, userIdToName): Promise<PolicyListItem[]> {
  const docs = await getDocuments(companyID, {
    documentType: 'policy'
  });

  console.log('[policyService]', 'getDocPolicies', docs);

  return docs.map((doc) => {
    // Helper to parse dates
    const parseDate = (val: any): Date | null => {
      if (!val) return null;
      if (val instanceof Timestamp) return val.toDate();
      if (val instanceof Date) return val;
      if (typeof val === 'object' && typeof val.seconds === 'number') return new Date(val.seconds * 1000);
      if (typeof val === 'string' || typeof val === 'number') {
        const d = new Date(val);
        return isNaN(d.getTime()) ? null : d;
      }
      return null;
    };
    
    return {
      id: doc.docID?.toString() || '',
      docID: doc.docID?.toString() || '',
      name: (doc as any).displayName || doc.fileName || 'Untitled Policy',
      policy_status: convertStatus(doc.status) || 'Draft',
      status: convertStatus(doc.status) || 'Draft',
      policy_id: doc.docID?.toString() || '',
      policy_name: (doc as any).displayName || doc.fileName || 'Untitled Policy',
      version: '1.0', // Default version since it's required but not in doc
      category: doc.documentType || 'Policy',
      policyCategory: doc.documentType || 'Policy',
      policy_version: doc.version || '1.0',
      // Include file information for downloads
      fileName: doc.fileName || '',
      file_name: doc.fileName || '',
      displayName: (doc as any).displayName || doc.fileName || '',
      last_reviewed: parseDate(doc.lastReviewDate) || parseDate(doc.dateUploaded),
      created: parseDate(doc.dateUploaded) || new Date(),
      owner: doc.uploadedBy || window.activeTenancy.tenancyOwner || 'Unknown',
      uploadedBy: doc.uploadedBy || '',
      // Review dates for compliance calendar
      nextReviewDate: parseDate(doc.nextReviewDate),
      addToCalendar: (doc as any).addToCalendar ?? false,
      repeat: (doc as any).repeat || null,
      policy_history: {
        policy_owner: doc.uploadedBy || window.activeTenancy.tenancyOwner || 'Unknown',
        created_date: parseDate(doc.dateUploaded) || new Date(),
        last_reviewed: parseDate(doc.lastReviewDate) || parseDate(doc.dateUploaded),
        nextReviewDate: parseDate(doc.nextReviewDate)
      },
      policy_metadata: {
        policyType: "document",
        documentCategory: "policy",
        lastEdited: parseDate(doc.dateUploaded) || new Date(),
        file_name: doc.fileName || ''
      },
      componentID: doc.componentId || '',
      relatedControls: dedupeArray([...(doc.relatedControls || []), ...(doc.procRef || [])]),
      procRefs: dedupeArray([...(doc.relatedControls || []), ...(doc.procRef || [])]),
      assignedUsers: doc.assignedUsers || []
    };
  });
}

/**
 * Fetches documents marked as Procedure from the document library
 * Returns them in a format compatible with the policy list
 */
export async function getDocProcedures(companyID: string, userIdToName): Promise<PolicyListItem[]> {
  // Get all documents and filter for Procedure type
  // We fetch all and filter manually because documentType might be 'Procedure' (capital P)
  console.log('[policyService]', 'getDocProcedures - companyID:', companyID);
  const allDocs = await getDocuments(companyID, { documentType: 'Procedure' });
  
  // Filter for documents where documentType is 'Procedure' (case-insensitive)
  const docs = allDocs.filter(doc => {
    console.log('[policyService]', 'getDocProcedures - doc:', doc);
    const docType = (doc.documentType || '').toLowerCase();
    return docType === 'procedure';
  });

  console.log('[policyService]', 'getDocProcedures - all docs:', allDocs.length, 'filtered:', docs.length);

  return docs.map((doc) => ({
    id: doc.docID?.toString() || '',
    docID: doc.docID?.toString() || '',
    name: (doc as any).displayName || doc.fileName || 'Untitled Process',
    policy_status: convertStatus(doc.status) || 'Draft',
    status: convertStatus(doc.status) || 'Draft',
    policy_id: doc.docID?.toString() || '',
    policy_name: (doc as any).displayName || doc.fileName || 'Untitled Process',
    version: '1.0',
    category: 'Procedure',
    policyCategory: 'Procedure',
    policy_version: doc.version || '1.0',
    // Include file information for downloads
    fileName: doc.fileName || '',
    file_name: doc.fileName || '',
    displayName: (doc as any).displayName || doc.fileName || '',
    last_reviewed: doc.lastReviewDate instanceof Timestamp ? doc.lastReviewDate.toDate() :
      doc.lastReviewDate instanceof Date ? doc.lastReviewDate : new Date(),
    created: doc.dateUploaded instanceof Timestamp ? doc.dateUploaded.toDate() :
      doc.dateUploaded instanceof Date ? doc.dateUploaded : new Date(),
    owner: doc.uploadedBy || window.activeTenancy.tenancyOwner || 'Unknown',
    nextReviewDate: doc.nextReviewDate instanceof Timestamp ? doc.nextReviewDate.toDate() :
      doc.nextReviewDate instanceof Date ? doc.nextReviewDate : new Date(),
    policy_history: {
      policy_owner: doc.uploadedBy || window.activeTenancy.tenancyOwner || 'Unknown',
      created_date: doc.dateUploaded instanceof Timestamp ? doc.dateUploaded.toDate() :
        doc.dateUploaded instanceof Date ? doc.dateUploaded : new Date(),
      last_reviewed: doc.lastReviewDate instanceof Timestamp ? doc.lastReviewDate.toDate() :
        doc.lastReviewDate instanceof Date ? doc.lastReviewDate : new Date(),
      nextReviewDate: doc.nextReviewDate instanceof Timestamp ? doc.nextReviewDate.toDate() :
        doc.nextReviewDate instanceof Date ? doc.nextReviewDate : new Date()
    },
    policy_metadata: {
      policyType: "document",
      documentCategory: "procedure",
      lastEdited: doc.dateUploaded instanceof Timestamp ? doc.dateUploaded.toDate() :
        doc.dateUploaded instanceof Date ? doc.dateUploaded : new Date(),
      file_name: doc.fileName || ''
    },
    componentID: doc.componentId || '',
    // Business unit from document library
    businessUnit: (doc as any).businessUnit || '',
    fileUrl: (doc as any).fileUrl || (doc as any).downloadUrl || '',
    relatedControls: dedupeArray([...(doc.relatedControls || []), ...(doc.procRef || [])]),
    procRefs: dedupeArray([...(doc.relatedControls || []), ...(doc.procRef || [])]),
    assignedUsers: doc.assignedUsers || []
  }));
}

/**
 * Converts a policy object to a document format and saves it using documentsService
 *
 * @param {Policy} policy - The policy to save
 * @param {string} companyID - The company ID
 * @returns {Promise<SupportingDocs.Document>} A promise that resolves to the saved document
 */
export async function savePolicyDocument(
  policy: Policy,
  companyID: string = window.companyID || ''
): Promise<SupportingDocs.Document> {
  // Create a document object with the policy data
  const document: any = {
    docID: policy.id,
    fileName: policy.policy_name || policy.file_name,
    documentType: policy.policyCategory || 'Policy',
    status: policy.policy_status || 'Draft',
    version: policy.policy_version || '1.0',
    lastReviewDate: policy.policy_history?.last_reviewed || null,
    nextReviewDate: policy.policy_history?.last_reviewed
      ? new Date(policy.policy_history.last_reviewed.getTime() + 365 * 24 * 60 * 60 * 1000) // 1 year from last review
      : new Date(),
    uploadedBy: getAuthUID() || policy.policy_history?.policy_owner || window.activeTenancy.tenancyOwner || '',
    dateUploaded: policy.policy_history?.created_date || new Date(),
    procRef: [...(policy.procRef || []), ...(policy.relatedControls || [])],
    relatedControls: [...(policy.procRef || []), ...(policy.relatedControls || [])],
    assignedUsers: policy.assignedUsers || [],
    classification: 'Business Confidential',
    comments: policy.policy_history?.reviewHistory?.map(
      review => `Reviewed by ${review.reviewedBy} on ${review.reviewedAt}`
    ).join('\n') || ''
  };

  // Add Confluence URL if available
  if (policy.policy_metadata?.confluenceURL) {
    document.confluenceURL = policy.policy_metadata.confluenceURL;
  }

  // Add file path if available
  if (policy.filePath) {
    document.filePath = policy.filePath;
  }

  try {
    // Check if this is an existing document or a new one
    let savedDoc;
    if (policy.id) {
      // For existing documents, we need to get the current document first to preserve any existing fields
      const currentDoc = await getDocument(policy.id, companyID);
      if (currentDoc) {
        // Update the existing document with new values
        Object.assign(currentDoc, document);
        savedDoc = await saveDocument(currentDoc, null, companyID);
      } else {
        // If document doesn't exist but has an ID, create a new one with that ID
        savedDoc = await createDocument(document as SupportingDocs.Document, null, companyID);
      }
    } else {
      // For new documents
      savedDoc = await createDocument(document as SupportingDocs.Document, null, companyID);
    }

    return savedDoc;
  } catch (error) {
    console.error('Error saving policy document:', error);
    throw error;
  }
}

// Helper function to check if a string is a valid Firebase document ID
const isValidFirebaseId = (id: string | undefined): boolean => {
  if (!id) return false;
  return /^[a-zA-Z0-9]{20,}$/.test(id);
};

export async function getPolicyList(companyID: string): Promise<PolicyListItem[]> {
  console.log('[policyService] getPolicyList via Thalassa API');
  
  const res = await deiterateAPI.GET('/v1/policies', {
    mode: 'cors',
    backend: 'thalassa',
    headers: {
      'X-Tenancy-Id': companyID,
      'Company-ID': companyID,
    }
  });
  
  const policies = (res.policies || []).map((policy: any) => {
    const resolvedOwner = (policy?.owner && isValidFirebaseId(policy.owner))
      ? policy.owner
      : (policy?.policy_history?.policy_owner && isValidFirebaseId(policy.policy_history.policy_owner))
        ? policy.policy_history.policy_owner
        : (window.activeTenancy?.tenancyOwner || 'Unknown') || 'Unknown';

    return {
      ...policy,
      owner: resolvedOwner,
      policy_history: {
        ...policy.policy_history,
        policy_owner: resolvedOwner,
        created_date: toDateSafe(policy.policy_history?.created_date),
        last_reviewed: toDateSafe(policy.policy_history?.last_reviewed) || toDateSafe(policy.policy_history?.created_date),
      },
      policy_metadata: policy.policy_metadata
        ? {
          ...policy.policy_metadata,
          lastEdited: toDateSafe(policy.policy_metadata?.lastEdited),
        }
        : {
          policyType: 'default' as const,
        },
    };
  });

  console.log('[policyService] getPolicyList returned', policies.length, 'policies');
  return policies;
}

/**
 * Soft-deletes a policy by moving it to the deletedPolicies collection
 * @param companyID - The company/tenant ID
 * @param policyId - The policy ID (policyRef) to delete
 * @returns Promise that resolves when the policy is moved to deletedPolicies
 */
export async function deletePolicy(companyID: string, policyId: string): Promise<void> {
  if (!companyID || !policyId) {
    throw new Error('Company ID and Policy ID are required');
  }
  
  const userId = getAuthUID() || 'unknown';
  
  console.log('[policyService] Deleting policy:', {
    companyID,
    policyId,
    userId,
    endpoint: `/v1/policies/${policyId}`
  });
  
  try {
    const response = await deiterateAPI.DELETE(`/v1/policies/${policyId}`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: {
        'X-Tenancy-Id': companyID,
        'Company-ID': companyID,
        'X-User-Id': userId,
      }
    });
    
    console.log('[policyService] Delete response:', response);
    console.log(`[policyService] Policy "${policyId}" deleted via Thalassa API`);
  } catch (error) {
    console.error('[policyService] Delete failed:', error);
    console.error('[policyService] Error details:', {
      status: error?.status,
      statusText: error?.statusText,
      message: error?.message,
      data: error?.data
    });
    throw error;
  }
}

// --- PRIVACY POLICY FUNCTIONS ---

import { encode as B64Encode, decode as B64Decode } from 'js-base64';

export interface PrivacyPolicy {
  content: string; // Markdown
  version: string;
  lastEdited: Date;
  editHistory?: Array<{
    editedBy: string;
    editedAt: Date;
    previousContent: string;
    previousVersion: string;
  }>;
}

export async function getPrivacyPolicy(companyID: string): Promise<PrivacyPolicy | null> {
  const docRef = doc(db, 'customers', companyID, 'product', 'policyPackages', 'privacyPolicy', 'policies', 'policy_current', 'privacy_policy');
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;
  const data = docSnap.data();
  const isBase64 = typeof data.policy_content === 'string' && data.policy_content.startsWith('data:');
  const content = typeof data.policy_content === 'string'
    ? (isBase64 ? B64Decode(data.policy_content.split(',')[1]) : data.policy_content)
    : '';
  return {
    content,
    version: data.current_version || '1.0',
    lastEdited: data.policy_history?.editHistory?.length
      ? new Date(data.policy_history.editHistory[data.policy_history.editHistory.length - 1].editedAt?.__time__ || Date.now())
      : new Date(data.created_date?.__time__ || Date.now()),
    editHistory: (data.policy_history?.editHistory || []).map((h: any) => ({
      editedBy: h.editedBy,
      editedAt: h.editedAt instanceof Timestamp ? h.editedAt.toDate() :
        cleanDateAndTimeObject(h.editedAt),
      previousContent: h.previousContent ? h.previousContent : '',
      previousVersion: h.previousVersion
    }))
  };
}

export async function savePrivacyPolicy(companyID: string, markdown: string, editorUID: string, prevVersion: string, prevContent: string): Promise<void> {
  const docRef = doc(db, 'customers', companyID, 'product', 'policyPackages', 'privacyPolicy', 'policies', 'policy_current', 'privacy_policy');
  const newVersion = autoIncrementSemanticVersion(prevVersion);
  // Firestore does not allow undefined values. Ensure safe defaults on first save.
  const editHistoryEntry = {
    editedBy: editorUID,
    editedAt: new Date(),
    previousContent: typeof prevContent === 'string' ? prevContent : '',
    previousVersion: prevVersion || '0.0',
  };

  // Fetch existing edit history
  const docSnap = await getDoc(docRef);
  let existingEditHistory: any[] = [];
  if (docSnap.exists()) {
    const data = docSnap.data();
    existingEditHistory = (data.policy_history?.editHistory || []).map((h: any) => ({
      ...h,
      // Defensive: handle Firestore Timestamp
      editedAt: h.editedAt instanceof Date ? h.editedAt : (h.editedAt?.toDate ? h.editedAt.toDate() : new Date()),
    }));
  }
  // Prepend new entry to editHistory (descending order)
  let newEditHistory = [editHistoryEntry, ...(existingEditHistory || [])];
  // Ensure descending order by editedAt (most recent first)
  newEditHistory = newEditHistory.sort((a, b) => {
    if (a.editedAt && b.editedAt) {
      return (b.editedAt?.getTime?.() || 0) - (a.editedAt?.getTime?.() || 0);
    }
    return 0;
  });

  await setDoc(docRef, {
    policy_content: `data:text/markdown;base64,${B64Encode(markdown)}`,
    policy_version: newVersion,
    current_version: newVersion,
    policy_history: {
      editHistory: newEditHistory
    }
  }, { merge: true });
}


export async function getPrivacyPolicyVersion(companyID: string): Promise<string> {
  const docRef = doc(db, 'customers', companyID, 'product', 'policyPackages', 'privacyPolicy', 'policies', 'policy_current', 'privacy_policy');
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return '1.0';
  return docSnap.data().policy_version || '1.0';
}

// --- END PRIVACY POLICY ---

// --- COLLECTION NOTICE FUNCTIONS ---

export interface CollectionNotice {
  content: string; // Markdown
  version: string;
  lastEdited: Date;
  editHistory?: Array<{
    editedBy: string;
    editedAt: Date;
    previousContent: string;
    previousVersion: string;
  }>;
}

export async function getCollectionNotice(companyID: string): Promise<CollectionNotice | null> {
  const docRef = doc(db, 'customers', companyID, 'product', 'policyPackages', 'collectionNotice', 'policies', 'policy_current', 'collection_notice');
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;
  const data = docSnap.data();
  const isBase64 = typeof data.policy_content === 'string' && data.policy_content.startsWith('data:');
  const content = typeof data.policy_content === 'string'
    ? (isBase64 ? B64Decode(data.policy_content.split(',')[1]) : data.policy_content)
    : '';
  return {
    content,
    version: data.current_version || '1.0',
    lastEdited: data.policy_history?.editHistory?.length
      ? new Date(data.policy_history.editHistory[data.policy_history.editHistory.length - 1].editedAt?.__time__ || Date.now())
      : new Date(data.created_date?.__time__ || Date.now()),
    editHistory: (data.policy_history?.editHistory || []).map((h: any) => ({
      editedBy: h.editedBy,
      editedAt: h.editedAt instanceof Timestamp ? h.editedAt.toDate() :
        cleanDateAndTimeObject(h.editedAt),
      previousContent: h.previousContent ? h.previousContent : '',
      previousVersion: h.previousVersion
    }))
  };
}

export async function saveCollectionNotice(companyID: string, markdown: string, editorUID: string, prevVersion: string, prevContent: string): Promise<void> {
  const docRef = doc(db, 'customers', companyID, 'product', 'policyPackages', 'collectionNotice', 'policies', 'policy_current', 'collection_notice');
  const newVersion = autoIncrementSemanticVersion(prevVersion);
  // Firestore does not allow undefined values. Ensure safe defaults on first save.
  const editHistoryEntry = {
    editedBy: editorUID,
    editedAt: new Date(),
    previousContent: typeof prevContent === 'string' ? prevContent : '',
    previousVersion: prevVersion || '0.0',
  };

  // Fetch existing edit history
  const docSnap = await getDoc(docRef);
  let existingEditHistory: any[] = [];
  if (docSnap.exists()) {
    const data = docSnap.data();
    existingEditHistory = (data.policy_history?.editHistory || []).map((h: any) => ({
      ...h,
      // Defensive: handle Firestore Timestamp
      editedAt: h.editedAt instanceof Date ? h.editedAt : (h.editedAt?.toDate ? h.editedAt.toDate() : new Date()),
    }));
  }
  // Prepend new entry to editHistory (descending order)
  let newEditHistory = [editHistoryEntry, ...(existingEditHistory || [])];
  // Ensure descending order by editedAt (most recent first)
  newEditHistory = newEditHistory.sort((a, b) => {
    if (a.editedAt && b.editedAt) {
      return (b.editedAt?.getTime?.() || 0) - (a.editedAt?.getTime?.() || 0);
    }
    return 0;
  });

  await setDoc(docRef, {
    policy_content: `data:text/markdown;base64,${B64Encode(markdown)}`,
    policy_version: newVersion,
    current_version: newVersion,
    policy_history: {
      editHistory: newEditHistory
    }
  }, { merge: true });
}

export async function getCollectionNoticeVersion(companyID: string): Promise<string> {
  const docRef = doc(db, 'customers', companyID, 'product', 'policyPackages', 'collectionNotice', 'policies', 'policy_current', 'collection_notice');
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return '1.0';
  return docSnap.data().policy_version || '1.0';
}

// --- END COLLECTION NOTICE ---

export async function getPolicy(id: string): Promise<Policy | null> {
  try {
    const companyID = window.companyID;
    
    const res = await deiterateAPI.GET(`/v1/policies/${id}`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: {
        'X-Tenancy-Id': companyID,
        'Company-ID': companyID,
      }
    });

    if (!res.policy) {
      return null;
    }

    const data = res.policy;

    return {
      ...data,
      owner: data?.owner || window.activeTenancy.tenancyOwner || 'Unknown',
      policy_history: {
        ...data.policy_history,
        policy_owner: data?.policy_history?.policy_owner || window.activeTenancy.tenancyOwner || 'Unknown',
        created_date: toDateSafe(data.policy_history?.created_date),
        last_reviewed: toDateSafe(data.policy_history?.last_reviewed) || toDateSafe(data.policy_history?.created_date),
      },
      policy_metadata: data.policy_metadata
        ? {
          ...data.policy_metadata,
          lastEdited: toDateSafe(data.policy_metadata?.lastEdited),
        }
        : {
          policyType: 'default',
        },
    };
  } catch (error) {
    console.error('Error fetching policy via Thalassa API:', error);
    throw error;
  }
}

export async function savePolicy(policy: Policy, fileToUpload: File | null, companyID: string): Promise<void> {
  console.log('[policyService]', 'savePolicy', policy, fileToUpload);
  const policiesCollection = collection(
    db,
    'customers',
    companyID,
    'product',
    'policyPackages',
    'policies',
  );

  const docRef = doc(policiesCollection, policy.id);

  console.log('[policyService]', 'savePolicy', policy, fileToUpload)
  // If there's a file to upload
  if (fileToUpload) {
    const storageRef = ref(storage, `/customers/${companyID}/policies/${policy.id}/${fileToUpload.name}`);
    try {
      // Upload the file
      await uploadBytes(storageRef, fileToUpload);

      // Get the download URL
      const downloadURL = await getDownloadURL(storageRef);

      // if (policy.policy_history?.policy_owner) {
      //   policy.owner = policy.policy_history.policy_owner;
      // }

      // Update policy metadata with the file URL
      policy.filePath = `/customers/${companyID}/policies/${policy.id}/${fileToUpload.name}`;
      policy.file_name = fileToUpload.name;
      policy.policy_metadata = {
        ...policy.policy_metadata,
        file_url: downloadURL,
        file_name: fileToUpload.name,
        lastEdited: new Date()
      };
    } catch (error) {
      console.error('Error uploading file:', error);
      throw new Error('Failed to upload policy file');
    }
  }

  let safePolicy = sanitizePolicyForWrite(policy);

  // To prevent the sanitizePolicyForWrite() from removing the meta for the DocumentReference
  if (policy.quiz_location) {
    safePolicy.quiz_location = policy.quiz_location;
  }

  // Preserve ReadTime and pagesCount fields
  if (policy.ReadTime !== undefined && policy.ReadTime !== null) {
    safePolicy.ReadTime = policy.ReadTime;
  }
  if (policy.pagesCount !== undefined && policy.pagesCount !== null) {
    safePolicy.pagesCount = policy.pagesCount;
  }

  await setDoc(docRef, safePolicy);
}

export async function reviewAllPolicies(companyID: string) {
  const user = auth.currentUser;
  const userIDToken = await user.getIdToken();
  console.log('[policyService]', 'reviewAllPolicies', companyID, userIDToken);

  const url = import.meta.env.VITE_cloudFunctionURL + '/deiterateInternalAPI/policies/reviewPolicies';
  console.log('[policyService]', 'reviewAllPolicies', url);
  return await fetch(
    url,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userIDToken}`,
        'Content-Type': 'application/json',
        'Company-ID': companyID,
      },
      body: JSON.stringify({
        customerID: companyID,
        reviewerID: auth.currentUser.uid,
        policyID: undefined,
      }),
    },
  );
}

export async function reviewPolicy(companyID: string, policyID: string) {
  const user = auth.currentUser;
  const userIDToken = await user.getIdToken();
  console.log('[policyService]', 'reviewPolicy', companyID, userIDToken);

  const url = import.meta.env.VITE_cloudFunctionURL + '/deiterateInternalAPI/policies/reviewPolicies';
  console.log('[policyService]', 'reviewPolicy', url);
  return await fetch(
    url,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userIDToken}`,
        'Content-Type': 'application/json',
        'Company-ID': companyID,
      },
      body: JSON.stringify({
        customerID: companyID,
        reviewerID: auth.currentUser.uid,
        policyID: policyID,
      }),
    },
  );
}

export async function getPolicyAssignments(companyID: string, policyRef: string, showAssignedUsers = true) {
  const ref = doc(
    db,
    'customers',
    companyID,
  );

  const snap = await getDoc(ref);
  const fullDoc = snap.data();
  const userList = Object.keys(fullDoc.customerOrg.users);

  const user = await auth.currentUser;
  const userIDToken = await user.getIdToken();
  console.log('[policyService]', 'getPolicyAssignments', companyID);

  const url = import.meta.env.VITE_cloudFunctionURL + '/deiterateInternalAPI/policies/checkPolicyAssignment'
  const policyAssignments = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${userIDToken}`,
      'Company-ID': companyID,
    },
    body: JSON.stringify({
      usersMap: fullDoc.customerOrg.users,
      policyID: policyRef,
    }),
  }).then((res) => res.json())
    .then((data) => {
      return data;
    });

  return policyAssignments.bulkIsAssignedResult
    .filter((item) => item.isAssigned === showAssignedUsers)
    .map((item) => item.userID);
}

export async function assignPolicyToAll(companyID: string, policyRef: string) {
  // Get unassigned users and assign them
  return getPolicyAssignments(companyID, policyRef, false)
    .then((userList) => {
      userList.forEach((user) => {
        assignPolicy(companyID, policyRef, user);
        console.log('assign policy to', policyRef, user);
      })
    });
}

export async function unassignPolicyToAll(companyID: string, policyRef: string) {
  // Get unassigned users and assign them
  return await getPolicyAssignments(companyID, policyRef, true)
    .then((userList) => {
      userList.forEach((user) => {
        unassignPolicy(companyID, policyRef, user);
        console.log('unassign policy to', policyRef, user);
      })
    });
}

export async function assignPolicy(companyID: string, policyRef: string, userUID: string) {
  console.log('[policyService]', 'assignPolicy', {
    companyID: companyID,
    policyRef: policyRef,
    userUID: userUID,
  });

  const user = await auth.currentUser;
  const userIDToken = await user.getIdToken();

  const url = import.meta.env.VITE_cloudFunctionURL + '/deiterateInternalAPI/policies/assignPolicy';
  return await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${userIDToken}`,
      'Company-ID': window.companyID,
    },
    body: JSON.stringify({
      uid: userUID,
      policyID: policyRef,
      companyID: companyID
    }),
  }).then((res) => res.json()).then((data) => {
    console.log('[policyService]', 'assignPolicy', {
      companyID: companyID,
      policyRef: policyRef,
      data: data,
      userUID: userUID,
    });
  })
}

export async function unassignPolicy(companyID: string, policyRef: string, userUID: string) {
  console.log('[policyService]', 'assignPolicy', {
    companyID: companyID,
    policyRef: policyRef,
    userUID: userUID,
  });

  const user = await auth.currentUser;
  const userIDToken = await user.getIdToken();

  const url = import.meta.env.VITE_cloudFunctionURL + '/deiterateInternalAPI/policies/unassignPolicy';
  return await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${userIDToken}`,
      'Content-Type': 'application/json',
      'Company-ID': companyID,
    },
    body: JSON.stringify({
      uid: userUID,
      policyID: policyRef,
      companyID: companyID,
    }),
  }).then((res) => res.json()).then((data) => {
    console.log('[policyService]', 'assignPolicy', {
      companyID: companyID,
      policyRef: policyRef,
      data: data,
      userUID: userUID,
    });
  })
}
